import { Link, useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard, BarChart3, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useUserCommunity } from "@/hooks/useUserCommunity";

import { APP_NAME } from "@/lib/constants";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { NotificationCenter } from "@/components/notifications/NotificationCenter";
import logo from "@/assets/business-pal-logo.png";

export function Header() {
  const navigate = useNavigate();
  const { user, profile, signOut, loading, isBusinessAdmin, isStaff, isSuperAdmin } = useAuth();
  const { data: userCommunity } = useUserCommunity();

  // Show community branding for all users with a community (except super admins who manage all communities)
  const showCommunityBranding = !!userCommunity && !isSuperAdmin;

  const getInitials = (name: string | null | undefined) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-16 items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-2">
          {showCommunityBranding && userCommunity.logo_url ? (
            <img
              src={userCommunity.logo_url}
              alt={userCommunity.name}
              className="h-8 w-8 rounded-lg object-cover"
              decoding="async"
            />
          ) : (
            <img
              src={logo}
              alt={APP_NAME}
              className="h-8 w-auto object-contain"
              width="32"
              height="32"
              decoding="async"
            />
          )}
          <span className="font-semibold tracking-tight hidden sm:inline-block">
            {showCommunityBranding ? userCommunity.name : APP_NAME}
          </span>
        </Link>

        <div className="flex items-center gap-2">
          {!loading && user ? (
            <>
              {/* Business Admin Panel Button */}
              {isBusinessAdmin && (
                <Button variant="outline" size="sm" asChild className="hidden sm:flex gap-2">
                  <Link to="/business">
                    <BarChart3 className="h-4 w-4" />
                    Business Dashboard
                  </Link>
                </Button>
              )}

              {/* Staff Panel Button */}
              {isStaff && !isBusinessAdmin && (
                <Button variant="outline" size="sm" asChild className="hidden sm:flex gap-2">
                  <Link to="/staff">
                    <LayoutDashboard className="h-4 w-4" />
                    Staff Portal
                  </Link>
                </Button>
              )}

              {/* Super Admin Button */}
              {isSuperAdmin && (
                <Button variant="outline" size="sm" asChild className="hidden sm:flex gap-2">
                  <Link to="/admin">
                    <LayoutDashboard className="h-4 w-4" />
                    Admin Panel
                  </Link>
                </Button>
              )}

              <NotificationCenter />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                    <Avatar className="h-8 w-8">
                      <AvatarImage 
                        src={profile?.avatar_url || undefined} 
                        className="object-cover"
                      />
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                        {getInitials(profile?.full_name)}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end">
                  <div className="flex flex-col space-y-1 p-2">
                    <p className="text-sm font-medium leading-none">
                      {profile?.full_name || "User"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {profile?.email}
                    </p>
                  </div>
                  <DropdownMenuSeparator />
                  
              {/* Mobile Admin Links */}
              {(isBusinessAdmin || isStaff || isSuperAdmin) && (
                    <>
                      <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                        Admin Access
                      </DropdownMenuLabel>
                      {isBusinessAdmin && (
                        <DropdownMenuItem asChild>
                          <Link to="/business" className="flex items-center gap-2">
                            <BarChart3 className="h-4 w-4" />
                            Business Dashboard
                          </Link>
                        </DropdownMenuItem>
                      )}
                      {isStaff && (
                        <DropdownMenuItem asChild>
                          <Link to="/staff" className="flex items-center gap-2">
                            <LayoutDashboard className="h-4 w-4" />
                            Staff Portal
                          </Link>
                        </DropdownMenuItem>
                      )}
                      {isSuperAdmin && (
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="flex items-center gap-2">
                            <LayoutDashboard className="h-4 w-4" />
                            Super Admin
                          </Link>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                    </>
                  )}

                  <DropdownMenuItem asChild>
                    <Link to={(isStaff || isBusinessAdmin) ? "/staff/profile" : "/profile"}>Profile</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/settings">Settings</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/mobile-app" className="flex items-center gap-2">
                      <Smartphone className="h-4 w-4" />
                      Mobile App
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive focus:bg-accent focus:text-accent-foreground"
                    onSelect={async () => {
                      try {
                        await signOut();
                        navigate("/login");
                      } catch (error) {
                        toast.error("Failed to sign out");
                      }
                    }}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : !loading ? (
            <div className="flex items-center gap-2">
              <Button variant="ghost" asChild>
                <Link to="/login">Sign in</Link>
              </Button>
              <Button asChild>
                <Link to="/signup">Get Started</Link>
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
